import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCurrentUser } from "shared/auth/hooks/useCurrentUser";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { verifyToken } from "shared/api/client/orvalHelpers";
import {
  isCreatorLayoutRole,
  normalizeUserRole,
  type AppUserRole,
} from "shared/auth/constants/roles";
import {
  getSidebarMenuForRole,
  type SidebarMenuItem,
} from "app/config/navigationByRole";
import { getMyOrganizations } from "features/account/api/account.api";

const DEFAULT_ORGANIZATION_NAME = "Poetics XR Studio";

function renderMenuIcon(item: SidebarMenuItem): React.ReactNode {
  if (!item.iconSrc) return null;
  return (
    <img
      className={item.iconClass}
      src={item.iconSrc}
      alt={item.iconAlt ?? item.label}
    />
  );
}

function getActiveItem(pathname: string, role: AppUserRole | null): string {
  const lower = pathname.toLowerCase();

  if (lower === "/home" || lower === "/home/home") return "home";
  if (lower.includes("/home/movie-review")) return "movie_review";
  if (lower.includes("/home/library")) return "library";
  if (lower.includes("/home/browse") || lower === "/browse") return "browse";
  if (lower.includes("/home/pair_headset")) return "pair_headset";
  if (lower.includes("/home/order_history")) return "order_history";
  if (lower.includes("/home/profile")) return "profile";
  if (lower.includes("/home/analytics")) return "analytics";
  if (lower === "/watchlist" || lower.includes("/home/watchlist")) return "watchlist";

  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] || "home";
  

  if (isCreatorLayoutRole(role) && lastSegment === "browse") return "browse";
  return lastSegment;
}

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const user = useCurrentUser();
  const role = normalizeUserRole(getCookie("userRole")) ?? "USER";
  const menuItems = getSidebarMenuForRole(role);
  const userFirstName = user?.firstName || "";
  const userLastName = user?.lastName || "";

  const [activeItem, setActiveItem] = useState(() =>
    getActiveItem(location.pathname, role)
  );
  const [organizationName, setOrganizationName] = useState(DEFAULT_ORGANIZATION_NAME);
  const platformLabel =
  role === "CURATOR" ? "CURATOR" : "CREATOR";
  useEffect(() => {
    setActiveItem(getActiveItem(location.pathname, role));
  }, [location, role]);

  useEffect(() => {
    if (!getCookie("userToken")) return;
    let active = true;
    getMyOrganizations()
      .then((organizations) => {
        if (!active) return;
        const name = organizations.find((org) => org.name.trim())?.name.trim();
        setOrganizationName(name || DEFAULT_ORGANIZATION_NAME);
      })
      .catch(() => {
        if (active) setOrganizationName(DEFAULT_ORGANIZATION_NAME);
      });
    return () => {
      active = false;
    };
  }, [user?.id]);

  const handleClick = async (item: SidebarMenuItem) => {
    if (!item.path || item.soon) {
      return;
    }

    const token = getCookie("userToken");
    if (!token) {
      navigate("/login");
      return;
    }
    const isVerified = await verifyToken(token);
    if (!isVerified) {
      navigate("/login");
      document.cookie =
        "userToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      return;
    }

    setActiveItem(item.key);
    navigate(item.path);
  };

  return (
    <div
      className={`user-sidebar ${isCollapsed ? "user-sidebar--collapsed" : ""} ${
        user ? `user-sidebar--${role.toLowerCase()}` : ""}`}
    >
      <button 
        className="sidebar-toggle" 
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label="Toggle sidebar"
      >
        <svg 
          className={`toggle-icon ${isCollapsed ? "" : ""}`}
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
      </button>
      <div className="user-sidebar-top-container">
        <div className={`user-sidebar-logo ${isCollapsed ? 'hidden' : ''}`} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6, paddingLeft: 10 }}>
          <img
            src="/images/poetics-stream-lockup.svg"
            alt="Poetics Stream"
            style={{ width: 174, height: 26, display: 'block' }}
          />
          {isCreatorLayoutRole(role) && (
            <span
              style={{
                fontFamily: "'Figtree', sans-serif",
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#6EA8FE',
                paddingLeft: 2,
              }}
            >
              {platformLabel} WORKSPACE
            </span>
          )}
        </div>
        <div className="user-sidebar-menu">
          <ul>
            {menuItems.map((item) => (
              <li
                key={item.key}
                onClick={() => handleClick(item)}
                className={[
                  activeItem === item.key ? "active" : "inactive",
                  item.soon ? "sidebar-item-disabled" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {renderMenuIcon(item)}
                <span className="menu-text">{item.label}</span>
                {item.soon && <span className="sidebar-soon-badge">SOON</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="sidebar-bottom-container">
        <div className="sidebar-user-icon-container">
          <div className="sidebar-user-icon">
            {userFirstName.charAt(0).toUpperCase()}
            {userLastName.charAt(0).toUpperCase()}
          </div>
        </div>
        <div className="sidebar-user-name-container">
          <div className="sidebar-organization">
            Organization: <span className="sidebar-organization-name">{organizationName}</span>
          </div>
          <div className="sidebar-user-name">
            {`${userFirstName} ${userLastName}`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;